import React from "react";
import styled from "styled-components";
import { OTAVehicle, VehicleTelemetry } from "../../types";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return "Never";
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function getStatusColor(status: string): string {
  switch (status) {
    case "online":
      return "#22c55e";
    case "offline":
      return "#6b7280";
    case "disconnected":
      return "#f59e0b";
    case "provisioned":
      return "#3b82f6";
    default:
      return "#9ca3af";
  }
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Panel = styled.div`
  background: #1e2433;
  border: 1px solid #2d3748;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 4px 0 8px 0;
`;

const PanelTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 14px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
`;

const MetricCard = styled.div`
  background: #111827;
  border-radius: 6px;
  padding: 10px 14px;
`;

const MetricLabel = styled.div`
  font-size: 10px;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const MetricValue = styled.div<{ $color?: string }>`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $color }) => $color ?? "#f1f5f9"};
  font-variant-numeric: tabular-nums;
`;

const MetricUnit = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: #6b7280;
  margin-left: 3px;
`;

const NoDataBadge = styled.div`
  font-size: 12px;
  color: #4b5563;
  font-style: italic;
`;

const Footer = styled.div`
  margin-top: 12px;
  font-size: 10px;
  color: #374151;
  display: flex;
  gap: 24px;
`;

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  vehicle: OTAVehicle;
}

const VehicleTelemetryPanel: React.FC<Props> = ({ vehicle }) => {
  const telemetry = vehicle.telemetryData as VehicleTelemetry | null;
  const cpuValue = telemetry?.cpuPercent ?? null;
  const memValue = telemetry?.memoryPercent ?? null;
  const diskGb = telemetry?.availableDiskSpaceGb ?? null;

  const hasMetrics = cpuValue != null || memValue != null || diskGb != null;

  return (
    <Panel>
      <PanelTitle>System Telemetry</PanelTitle>

      {!hasMetrics && (
        <NoDataBadge>
          No telemetry received yet — VehicleTelemetry publishes every 5 s after
          first connection.
        </NoDataBadge>
      )}

      <MetricsGrid>
        {cpuValue != null && (
          <MetricCard>
            <MetricLabel>CPU Usage</MetricLabel>
            <MetricValue $color={cpuValue > 80 ? "#ef4444" : "#f1f5f9"}>
              {cpuValue.toFixed(1)}
              <MetricUnit>%</MetricUnit>
            </MetricValue>
          </MetricCard>
        )}

        {memValue != null && (
          <MetricCard>
            <MetricLabel>Memory Usage</MetricLabel>
            <MetricValue $color={memValue > 80 ? "#ef4444" : "#f1f5f9"}>
              {memValue.toFixed(1)}
              <MetricUnit>%</MetricUnit>
            </MetricValue>
          </MetricCard>
        )}

        {diskGb != null && (
          <MetricCard>
            <MetricLabel>Free Disk Space</MetricLabel>
            <MetricValue
              // Thresholds mirror cloud-core's OTA_MINIMUM_DISK_SPACE_GB=150
              // (the pre-deployment disk check / stuck-deployment failure
              // threshold) — keep these two in sync if either side changes.
              $color={
                diskGb < 150 ? "#ef4444" : diskGb < 200 ? "#f59e0b" : "#f1f5f9"
              }
            >
              {diskGb.toFixed(1)}
              <MetricUnit>GB</MetricUnit>
            </MetricValue>
          </MetricCard>
        )}

        <MetricCard>
          <MetricLabel>Connection</MetricLabel>
          <MetricValue
            $color={getStatusColor(vehicle.connectionStatus)}
            style={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {vehicle.connectionStatus}
          </MetricValue>
        </MetricCard>
      </MetricsGrid>

      <Footer>
        <span>Last seen: {formatRelativeTime(vehicle.telemetryLastSeen)}</span>
        {vehicle.baselineDeploymentId && (
          <span>Baseline deployment: {vehicle.baselineDeploymentId}</span>
        )}
      </Footer>
    </Panel>
  );
};

export default VehicleTelemetryPanel;
